import { Pressable, StyleSheet, Text, View } from "react-native";
import type { Palette } from "@jackedlogs/shared";

// The desktop console nav rail (>= WIDE_BREAKPOINT). Pure navigation now: the
// brand + Create + account menu moved up to the TopBar, and the role switch
// into the account menu, so the rail carries only the surface tabs. On a phone
// the same nav lives in the top tab bar; on a wide viewport it becomes this
// left rail — the canonical responsive translation.
//
// Order mirrors mobile (Plan · Workouts · Progress). Settings is NOT a rail row
// anymore (it lives in the account menu); the shell filters it out of `items`.
export type SidebarItem = { key: string; label: string };

export function Sidebar({
  p,
  items,
  activeKey,
  onSelect,
}: {
  p: Palette;
  items: SidebarItem[];
  activeKey: string;
  onSelect: (key: string) => void;
}) {
  const styles = makeStyles(p);
  return (
    <View style={styles.rail}>
      <View style={styles.nav} accessibilityRole="tablist">
        {items.map((it) => {
          const on = it.key === activeKey;
          return (
            <Pressable
              key={it.key}
              style={[styles.item, on && styles.itemOn]}
              onPress={() => onSelect(it.key)}
              accessibilityRole="tab"
              accessibilityState={{ selected: on }}
              accessibilityLabel={it.label}
            >
              {/* Active cue is shape (left accent bar) + weight + colour, so
                  it never reads by colour alone (reduced-vision a11y). */}
              <View style={[styles.accent, on && styles.accentOn]} />
              <Text style={[styles.itemLabel, on && styles.itemLabelOn]}>
                {it.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const RAIL_WIDTH = 210;

function makeStyles(p: Palette) {
  return StyleSheet.create({
    rail: {
      width: RAIL_WIDTH,
      flexShrink: 0,
      height: "100%",
      backgroundColor: p.surface,
      borderRightWidth: 1,
      borderRightColor: p.border,
      paddingHorizontal: 12,
      paddingTop: 14,
      paddingBottom: 14,
    },
    nav: { gap: 2 },
    item: {
      flexDirection: "row",
      alignItems: "center",
      minHeight: 44,
      borderRadius: 10,
      paddingRight: 12,
    },
    itemOn: { backgroundColor: p.surface2 },
    accent: {
      width: 3,
      height: 20,
      borderRadius: 2,
      marginRight: 11,
      marginLeft: 4,
      backgroundColor: "transparent",
    },
    accentOn: { backgroundColor: p.brand500 },
    itemLabel: { color: p.ink3, fontSize: 15, fontWeight: "600" },
    itemLabelOn: { color: p.ink, fontWeight: "700" },
  });
}
